// offlineQueue.js
const LS_KEY = 'fm_outbox_v1';

const load = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; }
  catch { return []; }
};
const save = (ops) => localStorage.setItem(LS_KEY, JSON.stringify(ops));

// const opKey = (userGUID, setGUID) => `${userGUID}:${setGUID}`;

/**
 * Insert or replace an op for a given key.
 * Coalescing rules:
 *  - If a previous op exists for same `key`, replace it with the new one.
 *  - If you want DELETE to win over a prior UPSERT, the replacement already achieves that.
 */
export function enqueue(op) {
  const ops = load();
  if (op.key) {
    const filtered = ops.filter(o => o.key !== op.key);
    filtered.push({ ...op, ts: Date.now() });
    save(filtered);
  } else {
    ops.push({ ...op, ts: Date.now() });
    save(ops);
  }
}

export function clear() { save([]); }

export function getOps() { return load().sort((a,b) => a.ts - b.ts); }

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// simple exponential backoff
async function fetchWithRetry(url, options, attempts = 3) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(url, options);
      if (res.ok) return res;
      // Treat 4xx (except 429) as fatal (no retry)
      if (res.status < 500 && res.status !== 429) {
        throw new Error(`Fatal HTTP ${res.status}`);
      }
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      lastErr = e;
    }
    await sleep(Math.min(15000, 500 * 2 ** i) + Math.random()*300);
  }
  throw lastErr;
}

export async function flush() {
  if (!navigator.onLine) return;

  const ops = getOps();
  if (!ops.length) return;

  const remaining = [];
  for (const op of ops) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Idempotency-Key': crypto.randomUUID(),
        ...(op.headers || {})
      };
      const body = op.body ? JSON.stringify(op.body) : undefined;
      const res = await fetchWithRetry(op.url, { method: op.method, headers, body }, 2);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // success -> drop op
    } catch {
      // keep for next flush
      remaining.push(op);
    }
  }
  save(remaining);
}

// Hook flush to useful events
export function installFlushHooks() {
  const kick = () => flush();
  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') flush();
  });
  // Try once at startup
  flush();
}